/**
 * sincWaveFieldPipeline — concentric sinc(r) ripples expanding outward,
 * audio-reactive: bass = ripple intensity, mid = ripple speed, high =
 * sparkle. Iridescent color cycling with each wave front.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SincWaveFieldPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location=0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass, uMid, uHigh;

// File-scope audio globals — must be declared before any helper that uses them.
float bass;
float mid;
float high;

float hash21(vec2 p) {
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p, p.yx + 19.19);
  return fract((p.x + p.y) * p.x);
}

// sinc(x) = sin(x)/x, clamped to avoid the singularity.
float sinc(float x) {
  float ax = abs(x);
  if (ax < 0.001) return 1.0;
  return sin(x) / x;
}

// Radial sinc field — concentric rings radiating from center.
// Three "emitters" at moving positions inject ripple waves.
float waveField(vec2 p, float t) {
  float v = 0.0;
  // Main center emitter — pulsing radial sinc.
  float r = length(p);
  float scale = 11.0 + bass * 4.0;
  // Expand outward over time so we see the rings travel.
  float rAnim = r * scale - t * 4.0;
  v += sinc(rAnim) * (0.7 + bass * 0.6);

  // Three orbiting emitters add interference.
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    float a = t * (0.4 + fi * 0.13) + fi * 2.1;
    float orbitR = 0.55 + fi * 0.18;
    vec2 c = orbitR * vec2(cos(a + fi), sin(a * 0.83 + fi));
    float d = length(p - c);
    float dAnim = d * (8.0 + mid * 3.0) - t * (2.5 + mid * 2.0);
    // Use different uBass band per emitter so they're distinct.
    float band = (fi < 0.5) ? uBass.x : (fi < 1.5) ? uBass.y : uBass.z;
    v += sinc(dAnim) * (0.4 + band * 1.2) * 0.7;
  }
  return v;
}

// Iridescent color from a phase value.
vec3 iridescent(float x) {
  return 0.5 + 0.5 * cos(6.28318 * (x + vec3(0.0, 0.33, 0.67)));
}

void main() {
  // Audio bands (file-scope globals).
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid  = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid * 3.0, 0.0, 1.0), 0.75);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.8);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float v = waveField(p, uTime);

  // Color: alternating bands based on sign, iridescent phase = position + time.
  float phase = length(p) * 1.2 + uTime * 0.25 + bass * 1.5;
  vec3 posColor = iridescent(phase);
  vec3 negColor = iridescent(phase + 0.5);
  // Smoother blend across zero crossings.
  float w = smoothstep(-0.4, 0.4, v);
  vec3 col = mix(negColor, posColor, w);

  // Brightness from |v|, with extra punch from bass.
  // Use power curve to sharpen the lobes — only the brightest parts of each sinc ring read clearly.
  float sharpV = sign(v) * pow(abs(v), 0.5);
  col *= 0.25 + abs(sharpV) * 1.1 + bass * 0.25;

  // Mid: brighten the leading edge of each wave front.
  float edge = smoothstep(0.0, 0.4, v) - smoothstep(0.4, 0.9, v);
  col += vec3(0.95, 0.85, 1.0) * edge * (0.1 + mid * 0.4);

  // High-frequency sparkles: tiny dots scattered where |v| is high.
  float spark = hash21(floor(gl_FragCoord.xy * 0.6) + floor(uTime * 8.0));
  float sparkleMask = step(0.94, spark) * smoothstep(0.4, 1.0, abs(v));
  col += vec3(1.0, 0.95, 0.8) * sparkleMask * (0.3 + high * 0.9);

  // Center radial highlight that pulses with bass — narrow and dim.
  float corePulse = exp(-dot(p, p) * (8.0 - bass * 4.0));
  col += vec3(1.0, 0.95, 1.0) * corePulse * (0.08 + bass * 0.3);

  // Vignette + tone map + gamma.
  float vig = 1.0 - dot(p, p) * 0.35;
  col *= clamp(vig, 0.0, 1.0);
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

function bands(t: number, o: Float32Array): void {
  for (let i = 0; i < 9; i++) {
    o[i] = 0.5 + 0.3 * Math.sin(t * (1.1 + i * 0.27) + i * 0.8) + 0.18 * Math.sin(t * (0.31 + i * 0.17));
  }
}

export function computeSincWaveFieldBands(t: number, out: Float32Array): void {
  bands(t, out);
}

export function computeSincWaveFieldRealtimeBands(f: Uint8Array, out: Float32Array): void {
  const N = f.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += f[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSincWaveFieldPipeline(gl: WebGL2RenderingContext): SincWaveFieldPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return { program, vao, vbo, bands: new Float32Array(9) };
}

export function drawSincWaveField(
  gl: WebGL2RenderingContext,
  pipe: SincWaveFieldPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}

export { VERT_SRC, FRAG_SRC };